import React from 'react';
import { useForm } from 'react-hook-form';
import QRCode from 'react-qr-code';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateExternalWalletAddress, useFee } from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';
import { ListResponse, TransactionMethod, Wallet } from 'types';

import {
  BankTransferHint,
  Button,
  CopyClipBoard,
  Input,
  Spinner,
} from 'components';
import { yup } from 'utils';

function addressFromCreateResponse(data: unknown): string | undefined {
  if (data === null || typeof data !== 'object') {
    return undefined;
  }
  const row = data as Record<string, unknown>;
  if (typeof row.address === 'string' && row.address.length > 0) {
    return row.address;
  }
  if (typeof row.wallet_address === 'string' && row.wallet_address.length > 0) {
    return row.wallet_address;
  }
  return undefined;
}

type CryptoTabProps = {
  wallet?: Wallet;
  refetchWallet: () => Promise<unknown>;
  trMethods: ListResponse<TransactionMethod> | undefined;
  isSuccessTrMethods: boolean;
  isFetchingTrMethods: boolean;
  isLoadingTrMethods: boolean;
};

const CryptoTab: React.FC<CryptoTabProps> = ({
  wallet,
  refetchWallet,
  trMethods,
  isSuccessTrMethods,
  isFetchingTrMethods,
  isLoadingTrMethods,
}) => {
  const { t } = useTranslation('component.deposit.crypto');
  const [searchparams, setSearchParams] = useSearchParams();

  const schema = yup.object().shape({
    wallet: yup.string().label(t('form.wallet')).required(),
    transaction_method: yup
      .string()
      .label(t('form.transaction_method'))
      .required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const onSubmit = async () => {
    //
  };

  const formProps = useInputAttributes(register, errors, {
    wallet: t('form.wallet'),
    transaction_method: t('form.transaction_method'),
  });

  const trMethod =
    trMethods?.list.find((item) => item.id === watch('transaction_method')) ||
    trMethods?.list[0] ||
    null;

  const {
    mutate: createExternalWalletAddress,
    isLoading: isCreatingExternalAddress,
  } = useCreateExternalWalletAddress();
  const [createdAddressOverride, setCreatedAddressOverride] = React.useState<{
    transactionMethodId: string;
    address: string;
  } | null>(null);
  const [isRefreshingWalletAfterCreate, setIsRefreshingWalletAfterCreate] =
    React.useState(false);

  React.useEffect(() => {
    if (createdAddressOverride === null || wallet === undefined) {
      return;
    }
    const methodId = createdAddressOverride.transactionMethodId;
    const existsInWallet = wallet.external_wallet_addresses.some(
      (item) => item.transaction_method === methodId
    );
    if (existsInWallet) {
      setCreatedAddressOverride(null);
    }
  }, [wallet, createdAddressOverride]);

  const requestWalletAddress = React.useCallback(() => {
    if (wallet?.id === undefined || trMethod?.id === undefined) {
      return;
    }
    createExternalWalletAddress(
      { wallet_id: wallet.id, transaction_method_id: trMethod.id },
      {
        onSuccess: (data) => {
          const addr = addressFromCreateResponse(data);
          if (addr !== undefined && trMethod.id !== undefined) {
            setCreatedAddressOverride({
              transactionMethodId: trMethod.id,
              address: addr,
            });
          }
          setIsRefreshingWalletAfterCreate(true);
          void refetchWallet().finally(() => {
            setIsRefreshingWalletAfterCreate(false);
          });
        },
      }
    );
  }, [wallet?.id, trMethod?.id, createExternalWalletAddress, refetchWallet]);

  const transactionMethodList =
    trMethods?.list?.map((tr) => ({
      label: tr.title,
      value: tr.id,
      key: tr.key,
    })) || [];

  React.useEffect(() => {
    if (isSuccessTrMethods && transactionMethodList.length > 0) {
      setValue('transaction_method', transactionMethodList[0].value);
    }
    setSearchParams(searchparams);
  }, [isSuccessTrMethods, isFetchingTrMethods]);

  const {
    data: fee,
    isLoading: isLoadingFee,
    isFetching: isFetchingFee,
  } = useFee({
    queries: {
      currency: wallet?.currency.id || '',
      transaction_method: trMethod?.id || '',
    },
    enabled: Boolean(trMethod?.id) && Boolean(wallet),
  });

  const selectedTransactionMethodId =
    watch('transaction_method') || trMethod?.id || '';

  const cryptoWalletAddressFromWallet = wallet?.external_wallet_addresses.find(
    (item) => item.transaction_method === selectedTransactionMethodId
  )?.address;

  const cryptoWalletAddress =
    cryptoWalletAddressFromWallet ??
    (createdAddressOverride?.transactionMethodId === selectedTransactionMethodId
      ? createdAddressOverride.address
      : undefined);

  return (
    <Box w="100%">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          flexDir={{ base: 'column', md: 'row' }}
          w="100%"
          pb={0}
          gap={25}
        >
          <Box w={{ base: '100%', md: '50%' }}>
            <Input
              {...formProps.transaction_method}
              isSelectBox
              options={transactionMethodList}
              isDisabled={transactionMethodList.length < 1}
            />
          </Box>
        </Box>
        {isLoadingTrMethods && (
          <Stack w="100%" align="center" justify="center">
            <Spinner size="md" />
          </Stack>
        )}
        {trMethod?.fee !== undefined && (
          <Box px={0} py={0} display="flex">
            <Text color="body2">{t('fee')}</Text>
            {trMethod?.fee !== undefined && (
              <Text pl={0}>{trMethod?.fee ? '% ' + trMethod?.fee : '0'}</Text>
            )}
          </Box>
        )}
        {isLoadingFee && isFetchingFee && (
          <Stack w="100%" align="center" justify="center" py={0}>
            <Spinner size="md" />
          </Stack>
        )}
        {fee && wallet && (
          <Stack w="100%">
            <BankTransferHint
              maximum_credit={fee.maximum_credit}
              minimum_credit={fee.minimum_credit}
              maximum={fee.maximum}
              minimum={fee.minimum}
              percent={fee.percent}
              symbol={wallet.currency.symbol}
              type="deposit-crypto"
            />
          </Stack>
        )}
        <br />
        <Box bgColor="lightPrimary" p={4} textAlign="center">
          <Text color="warning">{t('hintTitle')}</Text>
          <Text color="warning">{t('hintDescription')}</Text>
        </Box>
        {wallet &&
          trMethod &&
          cryptoWalletAddress === undefined &&
          !isCreatingExternalAddress &&
          !isRefreshingWalletAfterCreate && (
            <Stack w="100%" align="center" py={6} px={8}>
              <Button
                type="button"
                variant="filled"
                title={t('requestWalletInfo')}
                onClick={requestWalletAddress}
                minW={{ base: '100%', md: '240px' }}
              />
            </Stack>
          )}
        {(isCreatingExternalAddress || isRefreshingWalletAfterCreate) &&
          wallet &&
          trMethod &&
          cryptoWalletAddress === undefined && (
            <Stack w="100%" align="center" justify="center" py={6}>
              <Spinner size="md" />
            </Stack>
          )}
        {cryptoWalletAddress !== undefined && (
          <Box p={8} textAlign="center">
            <Box
              display="flex"
              flexDir={{ base: 'column', xl: 'row' }}
              alignItems="center"
              justifyContent="space-between"
              gap={4}
            >
              <QRCode
                size={256}
                style={{ height: 'auto', maxWidth: '200px', width: '200px' }}
                value={cryptoWalletAddress}
                viewBox={'0 0 256 256'}
              />
              <CopyClipBoard initialValue={cryptoWalletAddress} full />
            </Box>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default CryptoTab;
